<template>
  <div :class="[cn(), { intersected: isIntersecting }]" ref="el" v-if="primaryImage">
    <a :class="cn('link')" :aria-label="product.title" :href="`/products/${product.handle}`">
      <ProductCardHallmark :value="hallmarkValue" />

      <div :class="cn('gallery')">
        <ShopifyImage :class="cn('image')" :src="primaryImage.src" ratio="5x7" />
        <ShopifyImage v-if="hoverImage" :class="cn('image')" :src="hoverImage.src" ratio="5x7" />
      </div>

      <div :class="cn('caption')">
        <h2 :class="cn('title')">
          <ProductCaptionTitle :title="product.title" />
        </h2>
        <ProductCardPricesLiquid class="prices" :product="product" />
      </div>
    </a>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { bem } from '../../utils/classnames'
import useIntersection from '../../composables/useIntersection'
import ShopifyImage from '../shopify/ShopifyImage.vue'
import ProductCaptionTitle from '../product/card/ProductCardTitle.vue'
import ProductCardPricesLiquid from '../product/card/ProductCardPricesLiquid.vue'
import ProductCardHallmark from '../product/card/ProductCardHallmark.vue'

const cn = bem('home-section-products-card')

const props = defineProps<{
  product: any
  index?: number
}>()

const el = ref(null)
const { isIntersecting } = useIntersection(el)

const images = computed(() => props.product?.images || [])
const primaryImage = computed(() => images.value?.[0] || props.product?.featured_image || null)
const hoverImage = computed(() => images.value?.[1] || null)
const hallmarkValue = computed(() => props.product?.metafields?.hallmark || null)
</script>

<style lang="scss" scoped>
@import '~styles/base';

.home-section-products-card {
  --caption-height: #{lineHeights(2)};

  transition: var(--transition-intersection);
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  overflow: visible;

  @include breakpoint('m+') {
    aspect-ratio: auto;
  }

  &:not(.intersected) {
    opacity: 0;
  }

  &__link {
    width: 100%;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    margin: 0;
    overflow: visible;
  }

  &__gallery {
    justify-content: flex-start;
    display: flex;
    width: 100%;
    margin: 0;

    > * {
      flex: 0 0 100%;
      transition: var(--transition-opacity);

      &:last-child:not(:only-child) {
        transform: translateX(-100%);
        outline-offset: 5px;
        object-fit: cover;
        opacity: 0;
      }
    }
  }

  &__image {
    display: block;
  }

  &__title {
    flex: 1;
    text-decoration: none;

    @include breakpoint('xl') {
    }
  }

  &__caption {
    display: flex;
    width: 100%;
    margin-top: 8px;

    @include breakpoint('xl') {
      display: block;
    }
  }

  :deep(.prices) {
    @media (min-width: 1001px) {
      text-align: right !important;
    }
  }

  :deep(.prices .product-card-prices__main) {
    @media (min-width: 1001px) {
      justify-content: flex-end !important;
    }
  }
}

@media (hover: hover) {
  *:hover,
  *:focus {
    > .home-section-products-card__gallery > :last-child {
      opacity: 1;
    }
  }
}
</style>
